import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoHiTrailer, LoHiTruck } from '../../../types';
import { validateTrimmed, valueTrimmed } from '../../../utilities/validateTrimmed';
import { AssetsService } from '../../assets.service';

interface EditTruckForm {
  truckID: string;
  name: string;
  owner: string | null;
  lessee: string | null;
  operational: boolean;
}

interface EditTrailerForm {
  trailerID: string;
  name: string;
  owner: string | null;
  lessee: string | null;
  operational: boolean;
}

interface TruckForm {
  truckID: string;
  description: string;
  notOperational: boolean;
}

interface TrailerForm {
  trailerID: string;
  description: string;
  notOperational: boolean;
}

@Component({
  selector: 'lohi-asset-management',
  templateUrl: './asset-management.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetManagementComponent implements OnInit {
  @ViewChild('truckMaint') private truckMaint: TemplateRef<any>;
  @ViewChild('trailerMaint') private trailerMaint: TemplateRef<any>;
  @ViewChild('addTruck') private addTruckModal: TemplateRef<any>;
  @ViewChild('addTrailer') private addTrailerModal: TemplateRef<any>;
  @ViewChild('editTruck') private editTruckModal: TemplateRef<any>;
  @ViewChild('editTrailer') private editTrailerModal: TemplateRef<any>;
  @ViewChild('confirmTruckArchive') private archiveTruckConfirmModal: TemplateRef<any>;
  @ViewChild('confirmTrailerArchive') private archiveTrailerConfirmModal: TemplateRef<any>;
  public trucks$: Observable<LoHiTruck[]>;
  public trailers$: Observable<LoHiTrailer[]>;
  public truckMainForm: FormGroup;
  public trailerMainForm: FormGroup;
  public editTruckForm: FormGroup;
  public editTrailerForm: FormGroup;
  public addingAsset$$ = new BehaviorSubject<boolean>(false);
  private addAssetRef: MatDialogRef<TemplateRef<any>>;

  constructor(private assetService: AssetsService, private matDialog: MatDialog, private fb: FormBuilder) {
    this.trucks$ = this.assetService.trucks$;
    this.trailers$ = this.assetService.trailers$;
  }

  public ngOnInit(): void {}

  public editTruckPrompt(truck: LoHiTruck) {
    this.editTruckForm = this.fb.group({
      truckID: [truck.truckId, Validators.required],
      originalName: [truck.truckName, []],
      inUse: [truck.inUse, []],
      name: [truck.truckName, [Validators.required, validateTrimmed]],
      owner: [truck.truckOwner],
      lessee: [truck.truckLessee],
      operational: [truck.truckStatus === 'operational', [Validators.required]],
    });
    this.matDialog.open(this.editTruckModal, {
      panelClass: ['w-full', 'h-1/2', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public archiveTruckPrompt() {
    this.matDialog.open(this.archiveTruckConfirmModal, {
      autoFocus: false,
    });
  }

  public archiveTruckSave() {
    if (!this.editTruckForm) {
      return;
    }
    const truckID = (this.editTruckForm.value as EditTruckForm).truckID;
    this.assetService.archiveTruck(truckID);
    this.matDialog.closeAll();
  }

  public async editTruckSave() {
    if (this.editTruckForm.invalid) {
      return;
    }
    const value = this.editTruckForm.value as EditTruckForm;
    await this.assetService.editTruck(
      value.truckID,
      value.name,
      value.operational,
      valueTrimmed(value.owner),
      valueTrimmed(value.lessee),
    );
    this.matDialog.closeAll();
  }

  // trailer edit

  public editTrailerPrompt(truck: LoHiTrailer) {
    this.editTrailerForm = this.fb.group({
      trailerID: [truck.trailerId, Validators.required],
      originalName: [truck.trailerName, []],
      inUse: [truck.inUse, []],
      name: [truck.trailerName, [Validators.required, validateTrimmed]],
      owner: [truck.trailerOwner],
      lessee: [truck.trailerLessee],
      operational: [truck.trailerStatus === 'operational', [Validators.required]],
    });
    this.matDialog.open(this.editTrailerModal, {
      panelClass: ['w-full', 'h-1/2', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public archiveTrailerPrompt() {
    this.matDialog.open(this.archiveTrailerConfirmModal, {
      autoFocus: false,
    });
  }

  public archiveTrailerSave() {
    if (!this.editTrailerForm) {
      return;
    }
    const truckID = (this.editTrailerForm.value as EditTrailerForm).trailerID;
    this.assetService.archiveTrailer(truckID);
    this.matDialog.closeAll();
  }

  public async editTrailerSave() {
    if (this.editTrailerForm.invalid) {
      return;
    }
    const value = this.editTrailerForm.value as EditTrailerForm;
    await this.assetService.editTrailer(
      value.trailerID,
      value.name,
      value.operational,
      valueTrimmed(value.owner),
      valueTrimmed(value.lessee),
    );
    this.matDialog.closeAll();
  }

  public async addTruckPrompt() {
    if (this.addAssetRef) {
      this.addAssetRef.close();
      this.addAssetRef = null;
    }
    this.addingAsset$$.next(false);
    this.addAssetRef = this.matDialog.open(this.addTruckModal, {
      panelClass: ['w-full', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public async addNewTruck(truckName: string) {
    if (!truckName) {
      return;
    }
    try {
      this.addingAsset$$.next(true);
      await this.assetService.addTruck(truckName);
      this.addAssetRef.close();
    } catch (error) {}
  }

  public async addTrailerPrompt() {
    if (this.addAssetRef) {
      this.addAssetRef.close();
      this.addAssetRef = null;
    }
    this.addingAsset$$.next(false);
    this.addAssetRef = this.matDialog.open(this.addTrailerModal, {
      panelClass: ['w-full', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public async addNewTrailer(trailerName: string) {
    if (!trailerName) {
      return;
    }
    try {
      this.addingAsset$$.next(true);
      await this.assetService.addTrailer(trailerName);
      this.addAssetRef.close();
    } catch (error) {}
  }

  public openTruckMaintenanceEventModal(id: string) {
    this.truckMainForm = this.fb.group({
      truckID: [id, Validators.required],
      description: [null, Validators.required],
      notOperational: [true, Validators.required],
    });

    this.matDialog.open(this.truckMaint, {
      panelClass: ['w-full', 'h-1/2', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public async addTruckMaintenanceEvent() {
    if (this.truckMainForm.invalid) {
      return;
    }
    const value = this.truckMainForm.value as TruckForm;
    this.assetService.putTruckMaintenanceEvent(value.truckID, value.description, value.notOperational);
    this.matDialog.closeAll();
  }

  public openTrailerMaintenanceEventModal(id: string) {
    this.trailerMainForm = this.fb.group({
      trailerID: [id, Validators.required],
      description: [null, Validators.required],
      notOperational: [true, Validators.required],
    });

    this.matDialog.open(this.trailerMaint, {
      panelClass: ['w-full', 'h-1/2', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public async addTrailerMaintenanceEvent() {
    if (this.trailerMainForm.invalid) {
      return;
    }
    const value = this.trailerMainForm.value as TrailerForm;
    this.assetService.putTrailerMaintenanceEvent(value.trailerID, value.description, value.notOperational);
    this.matDialog.closeAll();
  }

  public imageUrl(x: number, y: number): string {
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l-t+0fb0f0(${y},${x})/${y},${x},14/850x300?access_token=pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw`;
  }

  public imageUrlMobile(x: number, y: number): string {
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l-t+0fb0f0(${y},${x})/${y},${x},14/300x200?access_token=pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw`;
  }
}
